// src-mlr-chakra/components/footer/footer.component.js

import angular from 'angular';
import template from './footer.tpl.html';
import './footer.scss';

// --- Constants for clarity and easy maintenance ---
const DEFAULT_COUNTDOWN_SECONDS = 10;
const AFFID_PLACEHOLDER = 'AFFID=%%AFFID%%';

class FooterController {

  // Ensure all dependencies are correctly injected for minification
  static $inject = [
    "$window", "$timeout", "$ocLazyLoad", "AppService", "DayjsService", "PageService"
  ];

  /** @ngInject */
  constructor($window, $timeout, $ocLazyLoad, AppService, DayjsService, PageService) {

    Object.assign(this, {
      $window, $timeout, $ocLazyLoad, AppService, DayjsService, PageService
    });

    // --- Component State (initialize page to null/undefined) ---
    this.year = this.DayjsService.now().format('YYYY');
    this.page = undefined; // Will be populated in $onInit asynchronously
    this.queryParams = this.AppService.getQueryParamObject();
    this.isRedirecting = false;
    this.countdown = 0;
    this.redirectConfig = {};
    this.redirectLink = '';
  }

  // Make $onInit async to await the PageService call
  async $onInit() {
    // Fetch the page object from PageService. It might be a promise.
    this.page = await this.PageService.getGlobalPage();

    if (!this.page) {
      console.warn('FooterController: Page object not available from PageService.');
      return; // Exit if page data is not available
    }

    this.setupRedirect();
    this.initBeacon();
  }

  /**
   * A. REFACTORED: Consolidates all redirect logic into one clear workflow.
   */
  setupRedirect() {
    // 1. Determine redirect configuration from the (less ideal) custom HTML tag
    // Ensure this.page.body and this.page.options.redirect are available before use
    if (!this.page || !this.page.options || !this.page.options.redirect) {
      console.warn('FooterController: Redirect configuration missing in page object.');
      return;
    }
    this.extractRedirectConfigFromDOM();

    const { enable, datetime, countdown } = this.page.options.redirect;
    const hasRedirectBeenSuppressed = this.AppService.hasProperty(this.queryParams, 'redirect');

    // 2. Check if we should proceed with a redirect
    if (!enable || hasRedirectBeenSuppressed) {
      return;
    }

    const now = this.DayjsService.now().utc().valueOf();
    const redirectTime = this.DayjsService.parse(datetime).utc().valueOf();

    // 3. If it's time to redirect, decide how
    if (now > redirectTime) {
      this.redirectLink = this.getRedirectLink(this.page.options.redirect.to);

      if (countdown === false) {
        // Redirect immediately
        this.$window.location.href = this.redirectLink;
      } else {
        // Start the countdown modal
        this.isRedirecting = true;
        this.startCountdown();
        this.initModal('redirectmodal');
      }
    }
  }

  /**
   * Lazy-loads ModalService once, then opens the requested modal.
   * @param {string} [modalId='modal'] – Which modal container to show
   * (e.g. 'redirectmodal', 'upsellModal')
   */
  async initModal(modalId = 'modal') {
    // Re-use the same dynamic-import promise on every call
    if (!this._modalPromise) {
      this._modalPromise = import(
        /* webpackChunkName: "modalService" */ '@common/services/modal.service'
      );
    }

    // Wait for the chunk to finish loading
    const { ModalService } = await this._modalPromise;

    // Create—or reuse—our service instance if it’s already set to this ID
    if (!this.ms || this.ms.modalId !== modalId) {
      this.ms = new ModalService(modalId);
    }

    this.ms.initModal();  // open the modal
  }

  /**
   * Starts the recursive timeout for the countdown.
   */
  startCountdown() {
    // Initialize countdown only if it hasn't been set by extractRedirectConfigFromDOM
    if (this.countdown <= 0) { // Check for 0 or negative as well
      this.countdown = DEFAULT_COUNTDOWN_SECONDS;
    }

    const onTimeout = () => {
      if (this.countdown > 0) {
        this.countdown--;
        this.$timeout(onTimeout, 1000);
      } else {
        this.$window.location.href = this.redirectLink;
      }
    };
    this.$timeout(onTimeout, 1000);
  }

  /**
   * C. ROBUSTNESS: Simplified and more robust link generation.
   */
  getRedirectLink(baseUrl) {
    if (this.queryParams?.AFFID && baseUrl.includes(AFFID_PLACEHOLDER)) {
      return baseUrl.replace('%%AFFID%%', this.queryParams.AFFID);
    }
    // If no AFFID, remove the placeholder and any trailing '?' or '&'
    return baseUrl.replace(AFFID_PLACEHOLDER, '').replace(/[?&]$/, '');
  }

  /**
   * B. ASYNC/AWAIT: Cleaner lazy loading with async/await.
   */
  async initBeacon() {
    // Ensure this.page and its options are available
    if (this.page?.options?.beacon_id) {
      await this.$ocLazyLoad.load('beacon');
      this.$window.Beacon('init', this.page.options.beacon_id);
    }
  }

  /**
   * Extracts redirect configuration from the DOM, or sets default values if not found.
   * Relies on this.page.body being populated by $onInit.
   */
  extractRedirectConfigFromDOM() {
    // Initialize with default values first
    this.countdown = DEFAULT_COUNTDOWN_SECONDS;
    this.page.options = this.page.options || {}; // Ensure page.options exists
    this.page.options.redirect = this.page.options.redirect || {}; // Ensure page.options.redirect exists

    const defaultRedirectLink = this.getRedirectLink(this.page.options.redirect.to || '');
    this.page.options.redirect.title = 'Sorry! This offer is no longer available.';
    this.page.options.redirect.desc = `Please visit us at <a href="${defaultRedirectLink}" class="link-red" target="_self">${defaultRedirectLink}</a> to learn more.`;

    // Only proceed with DOM parsing if page.body exists
    if (this.page && this.page.body) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(this.page.body, 'text/html');
        const elm = doc.querySelector('page-redirects');

        if (elm) {
          // If element is found, override defaults with values from the DOM
          const delayAttribute = elm.getAttribute('delay');
          if (delayAttribute !== null) { // Check if attribute exists to differentiate from empty string/0
            const parsedDelay = parseInt(delayAttribute, 10);
            if (!isNaN(parsedDelay)) {
              this.countdown = parsedDelay;
            }
          }

          const titleAttribute = elm.getAttribute('title');
          if (titleAttribute !== null) {
            this.page.options.redirect.title = titleAttribute;
          }

          const descAttribute = elm.getAttribute('desc');
          if (descAttribute !== null) {
            this.page.options.redirect.desc = descAttribute;
          }
        }
      } catch (e) {
        console.error('FooterController: Error parsing page body for redirect config:', e);
        // If parsing fails, the default values set at the beginning will remain.
      }
    } else {
      console.warn('FooterController: Page body not available for redirect config extraction. Using default values.');
      // Default values are already set, so no further action needed here.
    }
  }
}

// --- Component Definition ---
class FooterComponent {
  constructor() {
    this.template = template;
    this.controller = FooterController;
  }
}

export default angular
  .module('mrsfooter.component', [])
  .component('mrsfooter', new FooterComponent())
  .name;